import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
const SLIDE_MS = 7000;
const KB = ["ss-kb0", "ss-kb1", "ss-kb2", "ss-kb3"];
/** Fullscreen animated slideshow: Ken Burns pan/zoom, crossfades, floating
 *  sparkles and background music (/media/music.mp3 if present, otherwise a
 *  built-in generative soft-piano arrangement via Web Audio). */
export default function Slideshow({ photos, title = "✨ Slideshow ✨", onClose }) {
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [audioOn, setAudioOn] = useState(true);
    const [cur, setCur] = useState(0); // active slot 0|1
    const curRef = useRef(0);
    const slotSrc = useRef([photos[0]?.url ?? "", ""]);
    const sparkleRef = useRef(null);
    const audioRef = useRef({ html: null, ac: null, gain: null, timer: null });
    const show = useCallback((i) => {
        if (!photos.length)
            return;
        const n = ((i % photos.length) + photos.length) % photos.length;
        const next = 1 - curRef.current;
        slotSrc.current[next] = photos[n].url;
        curRef.current = next;
        setCur(next);
        setIdx(n);
        for (let k = 1; k <= 2; k++) {
            const p = new Image();
            p.src = photos[(n + k) % photos.length].url;
        }
    }, [photos]);
    // auto-advance
    useEffect(() => {
        if (!playing)
            return;
        const t = setTimeout(() => show(idx + 1), SLIDE_MS);
        return () => clearTimeout(t);
    }, [idx, playing, show]);
    // keyboard
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight")
                show(idx + 1);
            if (e.key === "ArrowLeft")
                show(idx - 1);
            if (e.key === " ") {
                e.preventDefault();
                setPlaying((p) => !p);
            }
            if (e.key === "Escape")
                onClose();
        };
        addEventListener("keydown", onKey);
        return () => removeEventListener("keydown", onKey);
    }, [idx, show, onClose]);
    // sparkles
    useEffect(() => {
        const cv = sparkleRef.current;
        const cx = cv.getContext("2d");
        let raf = 0;
        const resize = () => {
            cv.width = innerWidth;
            cv.height = innerHeight;
        };
        resize();
        addEventListener("resize", resize);
        const parts = Array.from({ length: 70 }, () => ({
            x: Math.random() * innerWidth,
            y: Math.random() * innerHeight,
            r: Math.random() * 1.8 + 0.4,
            s: Math.random() * 0.35 + 0.08,
            ph: Math.random() * Math.PI * 2,
            hue: Math.random() < 0.5 ? 46 : 320,
        }));
        const tick = () => {
            cx.clearRect(0, 0, cv.width, cv.height);
            for (const p of parts) {
                p.y -= p.s;
                p.ph += 0.03;
                if (p.y < -5) {
                    p.y = cv.height + 5;
                    p.x = Math.random() * cv.width;
                }
                const a = 0.25 + 0.55 * Math.abs(Math.sin(p.ph));
                cx.beginPath();
                cx.arc(p.x + Math.sin(p.ph) * 8, p.y, p.r, 0, 7);
                cx.fillStyle = `hsla(${p.hue},90%,75%,${a})`;
                cx.shadowColor = `hsla(${p.hue},90%,70%,.8)`;
                cx.shadowBlur = 8;
                cx.fill();
            }
            raf = requestAnimationFrame(tick);
        };
        tick();
        return () => {
            cancelAnimationFrame(raf);
            removeEventListener("resize", resize);
        };
    }, []);
    // music: /media/music.mp3 if available, else generative Web Audio piano
    useEffect(() => {
        const a = audioRef.current;
        const startGenerative = () => {
            const ac = new AudioContext();
            const master = ac.createGain();
            master.gain.value = 0.16;
            master.connect(ac.destination);
            const verb = ac.createDelay(1);
            verb.delayTime.value = 0.31;
            const fb = ac.createGain();
            fb.gain.value = 0.38;
            verb.connect(fb);
            fb.connect(verb);
            verb.connect(master);
            a.ac = ac;
            a.gain = master;
            // Cmaj7 Am7 Fmaj7 G — warm & celebratory
            const prog = [
                [261.6, 329.6, 392, 493.9],
                [220, 261.6, 329.6, 392],
                [174.6, 220, 261.6, 329.6],
                [196, 246.9, 293.7, 392],
            ];
            let bar = 0;
            const note = (f, t, dur, vol) => {
                const o = ac.createOscillator(), g = ac.createGain();
                o.type = "triangle";
                o.frequency.value = f;
                const o2 = ac.createOscillator(), g2 = ac.createGain();
                o2.type = "sine";
                o2.frequency.value = f * 2;
                g2.gain.value = 0.25;
                g.gain.setValueAtTime(0, t);
                g.gain.linearRampToValueAtTime(vol, t + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
                o.connect(g);
                o2.connect(g2);
                g2.connect(g);
                g.connect(master);
                g.connect(verb);
                o.start(t);
                o.stop(t + dur + 0.1);
                o2.start(t);
                o2.stop(t + dur + 0.1);
            };
            const playBar = () => {
                if (ac.state === "closed")
                    return;
                const t = ac.currentTime + 0.05;
                const ch = prog[bar % 4];
                ch.forEach((f, i) => note(f / 2, t, 3.6, 0.12 - i * 0.015));
                [0, 1, 2, 3, 2, 1, 3, 2].forEach((n, i) => note(ch[n] * 2, t + i * 0.45, 1.4, 0.09));
                note(ch[0] / 4, t, 3.8, 0.14);
                bar++;
                a.timer = setTimeout(playBar, 3600);
            };
            playBar();
        };
        const html = new Audio("/media/music.mp3");
        html.loop = true;
        html.volume = 0.55;
        html
            .play()
            .then(() => (a.html = html))
            .catch(() => startGenerative());
        html.addEventListener("error", () => {
            if (!a.ac)
                startGenerative();
        });
        return () => {
            a.html?.pause();
            if (a.timer)
                clearTimeout(a.timer);
            a.ac?.close();
            audioRef.current = { html: null, ac: null, gain: null, timer: null };
        };
    }, []);
    useEffect(() => {
        const a = audioRef.current;
        if (a.html)
            a.html.muted = !audioOn;
        if (a.gain)
            a.gain.gain.value = audioOn ? 0.16 : 0;
    }, [audioOn]);
    if (!photos.length)
        return null;
    const year = photos[idx]?.taken_at ? new Date(photos[idx].taken_at).getFullYear() : null;
    return (_jsxs("div", { className: "fixed inset-0 z-[90] bg-black select-none", children: [_jsx("style", { children: `
        .ss-kb0{animation:ss-kb0 9s ease-in-out both}@keyframes ss-kb0{from{transform:scale(1)}to{transform:scale(1.09)}}
        .ss-kb1{animation:ss-kb1 9s ease-in-out both}@keyframes ss-kb1{from{transform:scale(1.09)}to{transform:scale(1)}}
        .ss-kb2{animation:ss-kb2 9s ease-in-out both}@keyframes ss-kb2{from{transform:scale(1.05) translate(-1.6%,0)}to{transform:scale(1.05) translate(1.6%,0)}}
        .ss-kb3{animation:ss-kb3 9s ease-in-out both}@keyframes ss-kb3{from{transform:scale(1.05) translate(1.2%,1.2%)}to{transform:scale(1.05) translate(-1.2%,-1.2%)}}
      ` }), [0, 1].map((slot) => (_jsx("div", { className: "absolute inset-0 flex items-center justify-center overflow-hidden transition-opacity duration-[1600ms] ease-in-out", style: { opacity: cur === slot && slotSrc.current[slot] ? 1 : 0 }, children: slotSrc.current[slot] && (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute -inset-[8%] bg-cover bg-center", style: {
                                backgroundImage: `url("${slotSrc.current[slot]}")`,
                                filter: "blur(34px) brightness(.42) saturate(1.2)",
                            } }), _jsx("img", { src: slotSrc.current[slot], alt: "", className: `relative max-w-[92%] max-h-[92%] object-contain rounded-md shadow-2xl ${KB[(idx + slot) % 4]}` }, slotSrc.current[slot])] })) }, slot))), _jsx("canvas", { ref: sparkleRef, className: "fixed inset-0 pointer-events-none z-[94]" }), _jsx("div", { className: "fixed top-0 left-0 h-[3px] z-[96] bg-gradient-to-r from-amber-300 to-pink-400 shadow-[0_0_10px_rgba(249,217,118,.7)] transition-[width] duration-300", style: { width: `${((idx + 1) / photos.length) * 100}%` } }), _jsx("div", { className: "fixed top-6 inset-x-0 text-center z-[95] pointer-events-none tracking-[.35em] text-amber-200/85 font-serif text-lg [text-shadow:0_2px_14px_rgba(0,0,0,.9)]", children: title }), year && (_jsx("div", { className: "fixed top-16 inset-x-0 text-center z-[95] pointer-events-none text-pink-200/70 tracking-[.25em] text-sm", children: year })), _jsxs("div", { className: "fixed bottom-7 right-8 z-[95] text-white/55 text-sm tracking-widest [text-shadow:0_1px_6px_#000]", children: [idx + 1, " / ", photos.length] }), _jsx("div", { className: "fixed bottom-5 inset-x-0 z-[95] flex justify-center gap-3 opacity-0 hover:opacity-100 transition-opacity duration-300", children: [
                    { label: "⟨", title: "Previous (←)", fn: () => show(idx - 1) },
                    { label: playing ? "❚❚" : "▶", title: "Play / Pause (space)", fn: () => setPlaying((p) => !p) },
                    { label: "⟩", title: "Next (→)", fn: () => show(idx + 1) },
                    { label: "♫", title: "Music on/off", fn: () => setAudioOn((m) => !m), dim: !audioOn },
                    { label: "✕", title: "Close (Esc)", fn: onClose },
                ].map((b) => (_jsx("button", { title: b.title, onClick: b.fn, className: `w-12 h-12 rounded-full border border-amber-200/35 bg-black/55 text-amber-100 text-lg backdrop-blur hover:bg-amber-200/25 hover:scale-110 transition-all ${b.dim ? "opacity-40" : ""}`, children: b.label }, b.title))) })] }));
}
